import { useState } from 'react';
import { motion } from 'framer-motion';
import { LogOut, MessageSquare, Image, User, Mail, RefreshCw } from 'lucide-react';
import AdminTestimonials from './admin/AdminTestimonials';
import AdminGallery from './admin/AdminGallery';
import AdminAbout from './admin/AdminAbout';
import AdminContact from './admin/AdminContact';

export default function AdminPage({ onLogout }) {
  const [activeTab, setActiveTab] = useState('testimonials');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const tabs = [
    { id: 'testimonials', label: 'Kundeomtaler', icon: MessageSquare },
    { id: 'gallery', label: 'Galleri', icon: Image },
    { id: 'about', label: 'Om Studio', icon: User },
    { id: 'contact', label: 'Kontakt', icon: Mail }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'testimonials':
        return <AdminTestimonials refreshTrigger={refreshTrigger} />
      case 'gallery':
        return <AdminGallery refreshTrigger={refreshTrigger} />
      case 'about':
        return <AdminAbout refreshTrigger={refreshTrigger} />
      case 'contact':
        return <AdminContact refreshTrigger={refreshTrigger} />
      default:
        return null
    }
  };

  return (
    <motion.div
      className="min-h-screen w-full bg-Pink-base px-4 py-8 md:py-12"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div className="max-w-6xl mx-auto" variants={containerVariants}>
        {/* Header */}
        <motion.div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-8 sm:mb-12" variants={itemVariants}>
          <div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-Neautral-main font-opensans">
              Admin Dashboard
            </h1>
            <p className="text-Neautral-light mt-2 text-sm sm:text-base">Studio Søraa administrasjon</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <motion.button
              onClick={handleRefresh}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-[15px] bg-blue-100 text-blue-700 font-semibold font-opensans hover:bg-blue-200 transition"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Refresh data from Sanity"
            >
              <RefreshCw size={20} strokeWidth={1.5} />
              Oppdater
            </motion.button>
            <motion.button
              onClick={onLogout}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-[15px] bg-red-100 text-red-700 font-semibold font-opensans hover:bg-red-200 transition"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <LogOut size={20} strokeWidth={1.5} />
              Logg ut
            </motion.button>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div className="mb-8 sm:mb-12" variants={itemVariants}>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 overflow-x-auto">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-t-[15px] font-semibold font-opensans text-sm sm:text-base transition whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-Neautral-white text-Pink-main'
                      : 'bg-Pink-BG text-Neautral-light hover:text-Neautral-main'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <TabIcon size={18} className="sm:block" strokeWidth={1.5} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden text-xs">{tab.label}</span>
                </motion.button>
              );
            })}
          </div>

          {/* Tab Content */}
          <motion.div
            className="bg-Neautral-white rounded-b-[15px] rounded-tr-[15px] px-4 sm:px-6 md:px-8 py-6 sm:py-8 shadow-[0px_25px_55px_0px_rgba(126,126,126,0.25)]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            key={activeTab}
          >
            {renderContent()}
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
